/**
 * Settings → Custom fields (MERCH-86, ADR 0009 §4): the editor's draft and what it sends.
 * Pure: no I/O. The server (lib/catalog/customAttributes) validates everything again.
 *
 * A draft row: { id, key?, label, kind, unit, choices (comma-separated), filter, saved }.
 * `kinds` is the server's list (Category.customAttributes `kinds`: [{ key, label, unit?, options? }]):
 * which kinds take a unit or choices is read from it, never named here (rule 13, SL-055).
 */

/** The server's entry for a kind ({} when unknown). */
export const kindOf = (kinds, key) => (kinds || []).find((k) => k.key === key) || {};

/** The saved fields → draft rows (choices as text, every row marked saved). */
export function draftOf(fields = []) {
  return (fields || []).map((f) => ({
    id: f.key, key: f.key, label: f.label || '', kind: f.kind, unit: f.unit || '', choices: (f.options || []).join(', '), filter: Boolean(f.filter), saved: true,
  }));
}

const splitChoices = (text) => String(text || '').split(',').map((s) => s.trim()).filter(Boolean);

/** Draft rows → the list Category.setCustomAttributes takes (a new row has no key: the server makes it from the label). */
export function fieldsOf(draft = [], kinds = []) {
  return (draft || []).map((f) => {
    const kind = kindOf(kinds, f.kind);
    const out = { label: String(f.label || '').trim(), kind: f.kind };
    if (f.saved && f.key) out.key = f.key;
    if (kind.unit && String(f.unit || '').trim()) out.unit = String(f.unit).trim();
    if (kind.options) out.options = splitChoices(f.choices);
    if (f.filter) out.filter = true;
    return out;
  });
}

/** What to fix before saving: { 'fields.<i>.<prop>': message, fields?: message }. */
export function draftErrors(draft = [], { max = 20, kinds = [] } = {}) {
  const e = {};
  if (draft.length > max) e.fields = `At most ${max} custom fields per product type.`;
  const seen = new Map();
  draft.forEach((f, i) => {
    const label = String(f.label || '').trim();
    if (!label) e[`fields.${i}.label`] = 'Name the field';
    const same = label.toLowerCase();
    if (label && seen.has(same)) e[`fields.${i}.label`] = `Another field is called "${label}"`;
    seen.set(same, i);
    if (kindOf(kinds, f.kind).options) {
      const choices = splitChoices(f.choices);
      const lower = choices.map((c) => c.toLowerCase());
      if (!choices.length) e[`fields.${i}.options`] = 'Add at least one choice';
      else if (new Set(lower).size !== lower.length) e[`fields.${i}.options`] = 'Each choice appears once';
    }
  });
  return e;
}

/** Move row `i` by `by` places (−1 up, +1 down); out of range leaves the list as it is. */
export function moveField(draft, i, by) {
  const j = i + by;
  if (j < 0 || j >= draft.length) return draft;
  const next = [...draft];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}

/** A 422's details ([{ path, message }]) → { path: message } for the rows. */
export function serverErrors(ex) {
  const details = (ex && (ex.details || (ex.response && ex.response.data && ex.response.data.details))) || [];
  return Object.fromEntries(details.map((d) => [String(d.path), d.message]));
}
